import React, { useState } from 'react';

import { useParams } from 'react-router-dom';

import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';

import { fetchLensScheduleFileUrl, useLensScheduleRun } from '~/hooks/apis/reporting/useLensSchedules';

const size = (bytes?: number) =>
  bytes === undefined
    ? ''
    : bytes >= 1048576
      ? `${(bytes / 1048576).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Where a scheduled Lens email links to when its PDFs were too large to attach. The schedule's
 * owner and the people it was sent to can download each PDF here; the link behind each button is
 * made fresh and lasts five minutes.
 */
export default function LensScheduleRunPage() {
  const { lensId = '', runId = '' } = useParams<{ lensId: string; runId: string }>();
  const run = useLensScheduleRun(lensId, runId);
  const [busyIndex, setBusyIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = async (index: number) => {
    setBusyIndex(index);
    setError(null);
    try {
      const { url } = await fetchLensScheduleFileUrl(lensId, runId, index);
      window.location.assign(url);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : 'The download could not start. Try again.');
    } finally {
      setBusyIndex(null);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {run.isLoading && <CircularProgress />}
      {run.isError && (
        <Alert severity="warning">
          This report is not available to you. Only the person who scheduled it and the people it was sent to can
          download it.
        </Alert>
      )}
      {run.data && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5">{run.data.schedule.name}</Typography>
            <Typography color="text.secondary" variant="body2">
              Scheduled Lens report, {run.data.run.runDate}
            </Typography>
          </Box>
          {run.data.run.files.map((file, index) =>
            file.error ? (
              <Alert key={index} severity="warning">
                {file.label}: not made ({file.error})
              </Alert>
            ) : (
              <Button
                key={index}
                disabled={busyIndex !== null}
                startIcon={busyIndex === index ? <CircularProgress size={16} /> : <DownloadIcon />}
                sx={{ justifyContent: 'flex-start' }}
                variant="outlined"
                onClick={() => void download(index)}
              >
                {file.fileName ?? file.label} {size(file.bytes) && `(${size(file.bytes)})`}
              </Button>
            ),
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      )}
    </Container>
  );
}
